import React, {
  Component,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from "react";
import AppLayoutToolbar from "@amzn/awsui-components-react/polaris/app-layout-toolbar";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import { SearchSideBar } from "../searchSideBar/searchSideBar";
import { useSelector } from "react-redux";
import { RootState } from "./rootReducer";
import Tabs from "@amzn/awsui-components-react/polaris/tabs";
import { Notification } from "../notification/notification";
import NoSsr from "./NoSsr";
import "./App.css";
import {
  addOrUpdateUrlParameter,
  getValueFromUrlParameter,
  TAB_ID_KEY,
} from "../../util/urlUtils";
import { CacheableNotification } from "../notification/cacheableNotification";
import { InitializeRum } from "../rum/initializeRum";
import { selectIsLoomAgentAssistantEnabled } from "../userInfo/userInfoSlice";
import { AppShell } from "../appShell/AppShell";
import { isV2Enabled } from "./featureFlag";
import { V2Toggle } from "./V2Toggle";
import { ToolbarPortal } from "./ToolbarPortal";
import { LegacyViewNotice } from "./LegacyViewNotice";
import { LEFT_NAV_ITEMS } from "../appShell/shellRoutes";
// The default "asins" tab table is imported EAGERLY. It is the LCP surface: on
// the empty default view the largest painted element is the table's empty-state
// welcome text, so lazy-loading this table only delays that paint (it adds a
// chunk fetch/eval hop before the welcome text can render). Keeping it eager
// lets the welcome text paint at first-paint time. The other tabs and the
// interaction-only features remain code-split below.
import { PorAsinListTable } from "../porAsinList/PorAsinListTable";

// Paths that only render inside App Shell. Non-flagged users landing
// on one of these get bounced to `/` (Legacy). `/stamper` is
// deliberately excluded: it is a shell nav entry in v2, but Legacy
// `<Content/>` also renders it via the `stamperView` branch, so a
// redirect would drop non-flagged users off a working page.
const LEGACY_INCOMPATIBLE_LEFT_NAV_PATHS = new Set<string>(["/stamper"]);
const APP_SHELL_TOP_LEVEL_PATHS = new Set<string>(
  LEFT_NAV_ITEMS.map((item) => item.path).filter(
    (path) => !LEGACY_INCOMPATIBLE_LEFT_NAV_PATHS.has(path)
  )
);

function isOnAppShellPath(): boolean {
  if (typeof window === "undefined") return false;
  const firstSegment =
    "/" + window.location.pathname.replace(/^\/+/, "").split("/")[0];
  return APP_SHELL_TOP_LEVEL_PATHS.has(firstSegment);
}

/** Preserve `search` + `hash` when redirecting to Legacy. */
function toLegacyUrl(): string {
  const { search, hash } = window.location;
  return "/" + search + hash;
}

// Non-active tabs are code-split so they do not evaluate before the initial
// paint of the default "asins" tab (the LCP surface). Each module uses a named
// export, so it is mapped onto the default export React.lazy expects.
const AsinValidationTable = lazy(() =>
  import("../validation/asinValidationTable").then((module) => ({
    default: module.AsinValidationTable,
  }))
);
const BaselineTable = lazy(() =>
  import("../dateBaseline/baselineTable").then((module) => ({
    default: module.BaselineTable,
  }))
);
const OneClickLaunchTable = lazy(() =>
  import("../oneClickLaunch/oneClickLaunchTable").then((module) => ({
    default: module.OneClickLaunchTable,
  }))
);

// Interaction-only features are code-split so their bundles never evaluate
// before the initial paint. They only load on demand: StamperWizard on the
// "/stamper" route, OclSubmissionTools when the submission-history drawer is
// opened, and LoomAgentAssistantWidget when the agent-assistant drawer (gated
// by isLoomAgentAssistantEnabled) is opened. All three use named exports, so
// each is mapped onto the default export React.lazy expects.
const StamperWizard = lazy(() =>
  import("../stampUtils/StamperWizard").then((module) => ({
    default: module.StamperWizard,
  }))
);
const OclSubmissionTools = lazy(() =>
  import("../oneClickLaunch/oneClickLaunchSubmissionTools").then((module) => ({
    default: module.OclSubmissionTools,
  }))
);
const LoomAgentAssistantWidget = lazy(() =>
  import("../../components/loom-agent-assistant/LoomAgentAssistantWidget").then(
    (module) => ({
      default: module.LoomAgentAssistantWidget,
    })
  )
);

const LoadingFallback = () => (
  <div
    className="awsui-util-t-c awsui-util-mt-l"
    data-testid="loading-fallback"
  >
    <Spinner size="large" />
  </div>
);

const Content = () => {
  const { isEditing } = useSelector((state: RootState) => state.porAsinList);
  const isLoomAgentAssistantEnabled = useSelector(
    selectIsLoomAgentAssistantEnabled
  );
  const [tab, setTab] = useState("asins");
  const [activeDrawerId, setActiveDrawerId] = useState<string | null>(null);
  const [navigationOpen, setNavigationOpen] = useState(true);
  const tabIdFromUrl = getValueFromUrlParameter(TAB_ID_KEY, "asins");
  useEffect(() => {
    setTab(tabIdFromUrl);
  }, [tabIdFromUrl]);

  // Controlled handlers required by AppLayoutToolbar for correct
  // toggle + responsive panel behavior.
  const handleNavigationChange = useCallback(
    ({ detail }: { detail: { open: boolean } }) => {
      setNavigationOpen(detail.open);
    },
    []
  );

  const handleDrawerChange = useCallback(
    ({ detail }: { detail: { activeDrawerId: string | null } }) => {
      setActiveDrawerId(detail.activeDrawerId);
    },
    []
  );

  //TODO: Implement proper router after refactoring query href logic
  const stamperView = window.location.href.split("/").slice(-1)[0] == "stamper";

  return (
    <div className="awsui" data-testid="app-content-wrapper">
      <InitializeRum />
      {/* Portal the toggle next to the drawer triggers so it shares
          the toolbar's sticky + rubber-band behavior. Matches V2 and
          avoids the hand-tuned fixed overlay that could drift off the
          ruler at different viewport widths. */}
      <ToolbarPortal>
        <V2Toggle />
      </ToolbarPortal>
      <AppLayoutToolbar
        data-testid="app-layout"
        headerSelector={"#mosaic-navigation"}
        footerSelector={"#footer"}
        navigationOpen={navigationOpen}
        onNavigationChange={handleNavigationChange}
        navigation={
          <NoSsr>
            <SearchSideBar />
          </NoSsr>
        }
        navigationHide={isEditing || tab === "launch" || stamperView}
        contentType={"table"}
        toolsHide={true}
        navigationWidth={300}
        activeDrawerId={activeDrawerId}
        onDrawerChange={handleDrawerChange}
        drawers={[
          ...(isLoomAgentAssistantEnabled
            ? [
                {
                  id: "agent-assistant",
                  ariaLabels: {
                    drawerName: "Loom Agent Assistant",
                    closeButton: "Close Loom Agent Assistant",
                    triggerButton: "Open Loom Agent Assistant",
                    resizeHandle: "Resize Loom Agent Assistant panel",
                  },
                  trigger: { iconName: "gen-ai" as const },
                  resizable: true,
                  defaultSize: 400,
                  content: (
                    <div
                      data-testid="agent-assistant-drawer"
                      style={{
                        height: "100%",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      <Suspense fallback={<LoadingFallback />}>
                        <LoomAgentAssistantWidget />
                      </Suspense>
                    </div>
                  ),
                },
              ]
            : []),
          ...(tab === "launch" && !stamperView
            ? [
                {
                  id: "submission-history",
                  ariaLabels: {
                    drawerName: "Submission History",
                    closeButton: "Close submission history",
                    triggerButton: "Open submission history",
                    resizeHandle: "Resize submission history panel",
                  },
                  trigger: { iconName: "status-info" as const },
                  resizable: true,
                  defaultSize: 420,
                  content: (
                    <div
                      data-testid="submission-history-drawer"
                      style={{
                        padding: "20px",
                      }}
                    >
                      <Suspense fallback={<LoadingFallback />}>
                        <OclSubmissionTools />
                      </Suspense>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
        content={
          <div id={"app-content"} data-testid="app-content">
            <SpaceBetween size="l" direction="vertical">
              {/* Notifications live inline above the tabs so they
                  render reliably. The previous `notifications` prop
                  with `stickyNotifications={true}` was intermittently
                  hidden (V2 hit the same issue in CR-301442811 and
                  moved to this pattern). */}
              <div data-testid="notifications-container">
                {!stamperView && <LegacyViewNotice />}
                <Notification />
                <CacheableNotification />
              </div>
              {stamperView ? (
                <Suspense fallback={<LoadingFallback />}>
                  <StamperWizard />
                </Suspense>
              ) : (
                <Tabs
                  data-testid="main-tabs"
                  activeTabId={tab}
                  tabs={[
                    {
                      label: "ASINs",
                      id: "asins",
                      content: <PorAsinListTable />,
                    },
                    {
                      label: "Validation",
                      id: "validation",
                      content: (
                        <Suspense fallback={<LoadingFallback />}>
                          <AsinValidationTable />
                        </Suspense>
                      ),
                    },
                    {
                      label: "Baseline",
                      id: "baseline",
                      content: (
                        <Suspense fallback={<LoadingFallback />}>
                          <BaselineTable />
                        </Suspense>
                      ),
                    },
                    {
                      label: "Launch",
                      id: "launch",
                      content: (
                        <Suspense fallback={<LoadingFallback />}>
                          <OneClickLaunchTable />
                        </Suspense>
                      ),
                    },
                  ]}
                  onChange={(event) => {
                    const tabId = event.detail.activeTabId;
                    addOrUpdateUrlParameter(TAB_ID_KEY, tabId);
                    setTab(tabId);
                  }}
                />
              )}
            </SpaceBetween>
          </div>
        }
      />
    </div>
  );
};

/**
 * Flagged: render `<AppShell/>` (owns its own routing).
 * Unflagged on a shell path: bounce to `/` preserving filter.
 * Unflagged elsewhere: Legacy `<Content/>`.
 */
class App extends Component {
  render() {
    if (isV2Enabled()) {
      return <AppShell />;
    }
    if (isOnAppShellPath()) {
      window.location.replace(toLegacyUrl());
      return null;
    }
    return <Content />;
  }
}

export default App;
